import { useQuery } from "@tanstack/react-query";
import { projects as staticProjects, testimonials as staticTestimonials } from "../lib/data";
import { projectBySlugQuery, projectsQuery, testimonialsQuery } from "../lib/queries";
import {
  isSanityConfigured,
  normalizeProject,
  normalizeTestimonial,
  sanityClient,
} from "../lib/sanity";

const FALLBACK_FEATURED_SLUGS = [
  "medipulse",
  "fifteen-ad-diwali",
  "inka-insurance",
  "loomistry",
  "no-reply",
  "nlu-taj",
];

function toProjects(docs) {
  if (!Array.isArray(docs)) return staticProjects;
  return docs.map(normalizeProject).filter(Boolean);
}

async function fetchProjects() {
  if (!isSanityConfigured || !sanityClient) return staticProjects;

  try {
    const docs = await sanityClient.fetch(projectsQuery);
    return toProjects(docs);
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error(
        "[Sanity] Failed to fetch projects. If you see a CORS error, add http://localhost:3000 under API → CORS origins in sanity.io/manage.",
        error,
      );
    }
    return staticProjects;
  }
}

async function fetchProjectBySlug(slug) {
  if (!isSanityConfigured || !sanityClient) {
    return staticProjects.find((p) => p.slug === slug) || null;
  }

  try {
    const doc = await sanityClient.fetch(projectBySlugQuery, { slug });
    return normalizeProject(doc) || staticProjects.find((p) => p.slug === slug) || null;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("[Sanity] Failed to fetch project:", slug, error);
    }
    return staticProjects.find((p) => p.slug === slug) || null;
  }
}

async function fetchTestimonials() {
  if (!isSanityConfigured || !sanityClient) return staticTestimonials;

  try {
    const docs = await sanityClient.fetch(testimonialsQuery);
    if (!Array.isArray(docs) || docs.length === 0) return staticTestimonials;
    return docs.map(normalizeTestimonial).filter(Boolean);
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("[Sanity] Failed to fetch testimonials:", error);
    }
    return staticTestimonials;
  }
}

const HOME_TESTIMONIAL_LIMIT = 4;

function pickHomepageTestimonials(list) {
  if (!Array.isArray(list) || list.length === 0) return [];
  const featured = list.filter((t) => t.featured);
  const pool = featured.length > 0 ? featured : list;
  return pool.slice(0, HOME_TESTIMONIAL_LIMIT);
}

export function useProjects() {
  return useQuery({
    queryKey: ["projects"],
    queryFn: fetchProjects,
    staleTime: 5 * 60 * 1000,
    placeholderData: staticProjects,
  });
}

export function useProject(slug) {
  return useQuery({
    queryKey: ["project", slug],
    queryFn: () => fetchProjectBySlug(slug),
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
    placeholderData: () => staticProjects.find((p) => p.slug === slug) || undefined,
  });
}

export function useFeaturedProjects() {
  const query = useProjects();
  const featured = isSanityConfigured
    ? (query.data || []).filter((p) => p.featured)
    : (query.data || []).filter((p) => FALLBACK_FEATURED_SLUGS.includes(p.slug));

  // If Sanity is configured but nothing is marked featured yet, keep homepage filled
  const data =
    isSanityConfigured && featured.length === 0
      ? (query.data || []).filter((p) => FALLBACK_FEATURED_SLUGS.includes(p.slug))
      : featured;

  return { ...query, data };
}

export function useTestimonials() {
  const query = useQuery({
    queryKey: ["testimonials"],
    queryFn: fetchTestimonials,
    staleTime: 5 * 60 * 1000,
    placeholderData: staticTestimonials,
  });

  return {
    ...query,
    data: pickHomepageTestimonials(query.data || staticTestimonials),
  };
}
